import { Box, Popover, List } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, Children } from 'react'

import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import NoteListItem from 'components/notes/common/NoteListItem'
import NoteSkeleton from 'components/notes/common/NoteSkeleton'
import { getSortedNotes, getArchivedAndUnarchivedNotes } from 'data/notes/notesSelectors'
import { Z_INDEX } from 'theme/zIndex'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'
import { memo } from 'utils/react'

const ViewNotesPopover = ({
  anchorEl,
  id,
  getCachedData = false,
  useLazyGetAllNotesQuery,
  position = 'left',
  onClose,
  onPaperMouseEnter,
  onPaperMouseLeave,
  showNoteVisibilityOnMobileIcon = false,
  ...rest
}) => {
  const isOpen = Boolean(anchorEl)
  const [getAllNotes, { isFetching: isGetAllNotesLoading, data: notesData }] = useLazyGetAllNotesQuery()
  const sortedNotes = getSortedNotes({ notes: get(notesData, 'notes', []) })
  const { unarchivedNotes: notes } = getArchivedAndUnarchivedNotes({ notes: sortedNotes })
  const hasNotes = notes.length > 0

  useEffect(() => {
    if (isOpen && id) {
      getAllNotes({ id }, getCachedData).unwrap().catch(handleError)
    }
  }, [isOpen, id])

  return (
    <Popover
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: position }}
      open={isOpen}
      slotProps={{
        paper: {
          // Click-through root, reachable paper: the mobile-visibility icon inside carries its own tooltip.
          sx: { width: 'max-content', pointerEvents: 'auto' },
          onMouseEnter: onPaperMouseEnter,
          onMouseLeave: onPaperMouseLeave,
        },
      }}
      sx={{ pointerEvents: 'none', zIndex: Z_INDEX.BADGE_POPOVER }}
      transformOrigin={{ vertical: 'top', horizontal: position }}
      onClose={onClose}
      {...rest}
    >
      {isGetAllNotesLoading && (
        <Box minWidth={300}>
          <NoteSkeleton rows={1} showActions={false} />
        </Box>
      )}
      {!isGetAllNotesLoading && (
        <>
          {!hasNotes && <HHSectionPlaceholder title="No notes" />}
          {hasNotes && (
            <List sx={{ py: 0 }}>
              {Children.toArray(
                notes.map((note, index) => (
                  <NoteListItem
                    note={note}
                    showDivider={notes.length - 1 !== index}
                    showNoteVisibilityOnMobileIcon={showNoteVisibilityOnMobileIcon}
                    showSecondaryActions={false}
                  />
                ))
              )}
            </List>
          )}
        </>
      )}
    </Popover>
  )
}

ViewNotesPopover.propTypes = {
  anchorEl: PropTypes.object,
  id: PropTypes.string.isRequired,
  getCachedData: PropTypes.bool,
  useLazyGetAllNotesQuery: PropTypes.func.isRequired,
  position: PropTypes.oneOf(['left', 'right']),
  onClose: PropTypes.func,
  showNoteVisibilityOnMobileIcon: PropTypes.bool,
  onPaperMouseEnter: PropTypes.func,
  onPaperMouseLeave: PropTypes.func,
}

export default memo(ViewNotesPopover)
